import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProductoService } from '../../core/services/producto.service';
import { CatalogoService } from '../../core/services/catalogo.service';
import { ImagenService } from '../../core/services/imagen.service';
import { AlertService } from '../../core/services/alert.service';
import { CatalogoItemApi } from '../../core/models/api/auth-api';
import { ProductoWebValidacionApi } from '../../core/models/api/producto-api';
import { Constante } from '../../constante';
import { PhotoGalleryEditorComponent, PhotoSlot } from '../../shared/components/photo-gallery-editor.component';
import { UiLoadingComponent } from '../../shared/components/ui-loading.component';

@Component({
    selector: 'app-admin-producto-form',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, PhotoGalleryEditorComponent, UiLoadingComponent],
    template: `
    <a routerLink="/admin/productos" class="inline-flex items-center gap-1 text-sm font-semibold text-rose-500 hover:underline"><svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>Productos</a>
    <div class="flex items-end justify-between gap-4 mt-2 mb-6">
      <div>
        <h2 class="text-2xl font-black text-gray-800">Configurar producto</h2>
        <p class="text-sm text-gray-400 mt-1">Nombre, SKU, precio, stock, descripción y categoría vienen de inventa. Aquí solo se configura lo que ve la web.</p>
      </div>
    </div>

    <app-ui-loading *ngIf="booting()"></app-ui-loading>

    <form *ngIf="!booting()" class="grid grid-cols-1 lg:grid-cols-12 gap-6" (ngSubmit)="guardar()">
      <section class="lg:col-span-5 bg-white rounded-[2rem] p-5 border border-rose-100 shadow-sm h-fit lg:sticky lg:top-24">
        <app-photo-gallery-editor
          [photos]="fotos()"
          (photosChange)="fotos.set($event)"
          title="Galería"
          hint="JPEG, PNG o WebP. La primera es la foto principal. Si no subes ninguna se usa la de inventa.">
        </app-photo-gallery-editor>
      </section>

      <section class="lg:col-span-7 flex flex-col gap-5">
        <div *ngIf="validacion() as v" class="rounded-[1.5rem] p-4 border text-sm" [class.border-amber-200]="v.advertencias.length > 0" [class.bg-amber-50]="v.advertencias.length > 0" [class.border-emerald-200]="v.advertencias.length === 0" [class.bg-emerald-50]="v.advertencias.length === 0">
          <p class="font-bold" [class.text-amber-700]="v.advertencias.length > 0" [class.text-emerald-700]="v.advertencias.length === 0">
            {{ v.advertencias.length > 0 ? 'Avisos de inventa' : 'Listo para web' }}
          </p>
          <ul *ngIf="v.advertencias.length > 0" class="list-disc ml-5 mt-1 text-amber-700">
            <li *ngFor="let aviso of v.advertencias">{{ aviso }}</li>
          </ul>
        </div>

        <div class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4">
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre (inventa)</span>
            <input [ngModel]="nombre()" name="nombre" readonly class="mt-1 w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-500 outline-none" />
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">SKU (inventa)</span>
            <input [ngModel]="sku()" name="sku" readonly class="mt-1 w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-500 outline-none" />
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Precio (inventa)</span>
            <input [ngModel]="precio()" name="precio" readonly class="mt-1 w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-500 outline-none" />
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Stock disponible (inventa)</span>
            <input [ngModel]="stockDisponible()" name="stock" readonly class="mt-1 w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-500 outline-none" />
          </label>
          <label class="block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Tipo (web)</span>
            <select [ngModel]="tipoProductoId()" (ngModelChange)="tipoProductoId.set($event)" name="tipoProductoId" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200 bg-white">
              <option *ngFor="let t of tipos()" [ngValue]="t.id">{{ t.nombre }}</option>
            </select>
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Slug (web)</span>
            <input [ngModel]="slug()" (ngModelChange)="slug.set($event)" name="slug" placeholder="Se genera desde el nombre si lo dejas vacío" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Tip (web)</span>
            <input [ngModel]="tip()" (ngModelChange)="tip.set($event)" name="tip" placeholder="Frase corta que aparece junto al precio" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200" />
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Promesa (web)</span>
            <textarea [ngModel]="promesa()" (ngModelChange)="promesa.set($event)" name="promesa" rows="2" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200"></textarea>
          </label>
          <label class="md:col-span-2 block">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wide">Info adicional (web)</span>
            <textarea [ngModel]="infoAdicional()" (ngModelChange)="infoAdicional.set($event)" name="infoAdicional" rows="3" class="mt-1 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-200"></textarea>
          </label>
        </div>

        <div class="bg-white rounded-[2rem] p-6 border border-rose-100 shadow-sm flex flex-wrap gap-5">
          <label class="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <input type="checkbox" [ngModel]="esNuevo()" (ngModelChange)="esNuevo.set($event)" name="esNuevo" class="accent-rose-500" /> Nuevo
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <input type="checkbox" [ngModel]="esDestacado()" (ngModelChange)="esDestacado.set($event)" name="esDestacado" class="accent-rose-500" /> Destacado
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700 font-medium">
            <input type="checkbox" [ngModel]="visibleCatalogo()" (ngModelChange)="visibleCatalogo.set($event)" name="visibleCatalogo" class="accent-rose-500" /> Visible en catálogo
          </label>
        </div>

        <div class="flex gap-3">
          <button type="submit" [disabled]="loading()" class="flex-1 py-3.5 rounded-2xl text-white font-bold text-sm disabled:opacity-60" style="background-color: var(--bg-main);">
            {{ loading() ? 'Guardando...' : 'Guardar configuración' }}
          </button>
          <a routerLink="/admin/productos" class="px-5 py-3.5 rounded-2xl text-sm font-bold text-gray-500 border border-gray-200 bg-white">Cancelar</a>
        </div>
      </section>
    </form>
    `
})
export class AdminProductoFormComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private productoService = inject(ProductoService);
    private catalogoService = inject(CatalogoService);
    private imagenService = inject(ImagenService);
    private alerts = inject(AlertService);

    tipo = signal('VARIANTE');
    uuid = signal<string | null>(null);
    nombre = signal('');
    sku = signal('');
    precio = signal(0);
    stockDisponible = signal(0);
    tipoProductoId = signal(0);
    slug = signal('');
    tip = signal('');
    promesa = signal('');
    infoAdicional = signal('');
    esNuevo = signal(false);
    esDestacado = signal(false);
    visibleCatalogo = signal(true);
    fotos = signal<PhotoSlot[]>([]);
    validacion = signal<ProductoWebValidacionApi | null>(null);

    tipos = signal<CatalogoItemApi[]>([]);
    booting = signal(true);
    loading = signal(false);

    ngOnInit(): void {
        const routeUuid = this.route.snapshot.paramMap.get('uuid');
        this.uuid.set(routeUuid ?? null);

        this.catalogoService.listTiposProducto().subscribe({
            next: (tipos) => {
                this.tipos.set(tipos);
                if (!this.tipoProductoId()) {
                    this.tipoProductoId.set(tipos.find((t) => t.codigo === 'PRODUCTO')?.id ?? tipos[0]?.id ?? 0);
                }
            }
        });

        const id = this.uuid();
        if (!id) {
            this.booting.set(false);
            return;
        }

        this.productoService.getProducto(id).subscribe({
            next: (p) => {
                if (p) {
                    this.tipo.set(p.tipo);
                    this.uuid.set(p.uuid);
                    this.nombre.set(p.nombre);
                    this.sku.set(p.sku);
                    this.precio.set(p.precio);
                    this.stockDisponible.set(p.stockDisponible ?? 0);
                    this.tipoProductoId.set(p.tipoProductoId);
                    this.slug.set(p.slug ?? '');
                    this.tip.set(p.tip ?? '');
                    this.promesa.set(p.promesa ?? '');
                    this.infoAdicional.set(p.infoAdicional ?? '');
                    this.esNuevo.set(!!p.esNuevo);
                    this.esDestacado.set(!!p.esDestacado);
                    this.visibleCatalogo.set(!!p.visibleCatalogo);
                    this.fotos.set((p.imagenes ?? []).map((i) => ({
                        previewUrl: i.urlMedium || i.urlOriginal,
                        imagenUuid: i.imagenUuid,
                        file: null
                    })));
                    this.productoService.validarProductoWeb({ tipo: p.tipo, id: p.uuid }).subscribe({
                        next: (validacion) => this.validacion.set(validacion)
                    });
                }
                this.booting.set(false);
            },
            error: () => {
                this.booting.set(false);
                this.alerts.error('No se pudo cargar el producto.');
            }
        });
    }

    guardar() {
        const id = this.uuid();
        if (!id) {
            this.alerts.error('Producto inválido.');
            return;
        }

        this.loading.set(true);
        const pending = this.fotos().filter((f) => !!f.file).map((f) => f.file as File);
        if (pending.length === 0) {
            this.enviar(id, this.fotos().map((f) => f.imagenUuid).filter((uuid): uuid is string => !!uuid));
            return;
        }

        this.imagenService.upload(pending).subscribe({
            next: (imgs) => {
                let i = 0;
                const uuids = this.fotos().map((f) => f.imagenUuid || imgs[i++]?.uuid).filter((uuid): uuid is string => !!uuid);
                this.enviar(id, uuids);
            },
            error: (err) => {
                this.loading.set(false);
                this.alerts.error(err?.message || 'No se pudieron subir las fotos.');
            }
        });
    }

    private enviar(id: string, imagenUuids: string[]) {
        const body = {
            tipo: this.tipo(),
            id,
            slug: this.slug() || null,
            tipoProductoId: this.tipoProductoId(),
            tip: this.tip() || null,
            promesa: this.promesa() || null,
            infoAdicional: this.infoAdicional() || null,
            esNuevo: this.esNuevo(),
            esDestacado: this.esDestacado(),
            visibleCatalogo: this.visibleCatalogo(),
            imagenUuids
        };

        this.productoService.configProducto(body).subscribe({
            next: (ok) => {
                this.loading.set(false);
                if (ok) {
                    this.alerts.ok('Configuración guardada');
                    this.router.navigate([Constante.URL_ADMIN + '/productos']);
                } else {
                    this.alerts.error('No se pudo guardar la configuración');
                }
            },
            error: () => {
                this.loading.set(false);
                this.alerts.error('No se pudo guardar la configuración');
            }
        });
    }
}
